import { useEffect } from "react";
import { ZoomIn, ZoomOut } from "lucide-react";

// Hour-row heights in px; 0 = "Fit" (08:00–21:00 fills the visible grid)
export const ZOOM_LEVELS = [28, 36, 48, 64, 80, 104];
const KEY = "iclean_cal_zoom";
const FIT_HOURS = 13;

export const loadZoom = () => {
  const v = localStorage.getItem(KEY);
  if (v === null) { return document.documentElement.classList.contains("force-desktop") ? 64 : 0; }
  return ZOOM_LEVELS.includes(Number(v)) ? Number(v) : 0;
};

// Sets --cal-row on the board; in Fit mode it is measured from the visible time grid.
export const useCalendarRow = (ref, zoom, deps = []) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) { return undefined; }
    const apply = () => {
      if (zoom) { el.style.setProperty("--cal-row", `${zoom}px`); return; }
      const content = el.querySelector(".rbc-time-content");
      if (!content) { return; }
      el.style.setProperty("--cal-row", `${Math.max(28, Math.floor((content.clientHeight - 8) / (FIT_HOURS + 0.3)))}px`);
    };
    apply();
    const toEight = () => {
      const content = el.querySelector(".rbc-time-content");
      const row = el.querySelector(".rbc-time-gutter .rbc-timeslot-group");
      if (content && row) { content.scrollTop = Math.round(row.getBoundingClientRect().height * 8); }
    };
    const t = setTimeout(() => { apply(); requestAnimationFrame(toEight); }, 50);
    window.addEventListener("resize", apply);
    return () => { clearTimeout(t); window.removeEventListener("resize", apply); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, zoom, ...deps]);
};

export const CalendarZoom = ({ zoom, setZoom }) => {
  const set = (z) => { setZoom(z); localStorage.setItem(KEY, String(z)); };
  const fitIdx = 2;
  const i = zoom ? ZOOM_LEVELS.indexOf(zoom) : fitIdx;
  const btn = "px-2 py-1.5 text-slate-600 hover:bg-slate-50 disabled:opacity-40";
  return (
    <div className="flex items-center rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="cal-zoom">
      <button className={btn} title="Zoom out (more hours)" disabled={zoom === ZOOM_LEVELS[0]}
        onClick={() => set(ZOOM_LEVELS[Math.max(0, zoom ? i - 1 : 1)])} data-testid="cal-zoom-out"><ZoomOut className="h-4 w-4" /></button>
      <button className={`px-2 py-1.5 text-xs font-semibold ${zoom === 0 ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-50"}`}
        onClick={() => set(0)} title="Fit 8am–9pm on screen" data-testid="cal-zoom-fit">Fit</button>
      <button className={btn} title="Zoom in (bigger)" disabled={zoom === ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
        onClick={() => set(ZOOM_LEVELS[Math.min(ZOOM_LEVELS.length - 1, zoom ? i + 1 : 3)])} data-testid="cal-zoom-in"><ZoomIn className="h-4 w-4" /></button>
    </div>
  );
};
